/* ═══════════════════════════════════════════════════════════════════════════════════════════════════════════
   INTO THE ENVELOPE, YONATAN'S (18 Oct 2026). Daniel: "Do envelope animation."

   Preview → Send in Builder B. Until today the card folded shut with Preview's own arithmetic fold and went into a
   CSS sleeve (builder-b.js _ppbIntoEnvelope, 12 Oct). Yonatan rendered the real thing in Blender on 14 Oct
   ("Website envelope animation.zip", README in the venture repo, output/2026-10-15-v2e-render/zip-envelope): after
   card-close, the closed card slides under the side flaps and the bottom flap of our kraft envelope, the top flap
   closes, the PICTUREPOPS sticker slaps on, and the envelope turns over and lands stamp side up. 122 frames, 30 fps.

   THE MOVE, in two clips that join exactly:
     1. card-close(-sm).mp4, the shaded close (video frame i = card-geometry.json frame i), the CSS planes posed per
        video frame and the shading multiplied over them from a canvas, as the opening does (configurator.js
        startOpening) and the camera moves do (card-moves.js). The 2D crop camera flies, over the same frames, from
        wherever Preview had it to the envelope's framing (the clip's whole 2000 square fitted to the stage).
        A card already shut on Preview skips this: the camera glides to the envelope's framing in 450 ms.
     2. the envelope clip, whose frame 0 IS card-close's last frame (same camera, same card). Bottom to top:
        the CoverFront plane posed per frame from envelope-geometry.json with the design's own art; the envelope
        video, normal blend, with alpha (it carries the envelope, the sticker, the table shadow and the card's
        shading); the Address plane, HTML, for the sender and the recipient, shown once its face comes up.
        The video: envelope(-sm).webm (VP9 alpha) in Chrome, Edge and Firefox, drawn into a 2D canvas; everywhere
        else (Safari, every iOS browser) envelope-alpha(-sm).mp4, colour | alpha side by side, through WebGL
        (Yonatan's drawEnv). Every other plane of the card is hidden for the clip: they stay at the closed pose
        while CoverFront travels, and the video holds the card's whole look.
   Then the landed envelope becomes the Send page's (Daniel, 18 Oct: "the same one as the render ... in one fluid
   motion"): the page's envelope is the clip's last frame (envelope-face.webp) with the addresses where the Address
   plane writes them, so a copy of it replaces the clip in place, glides into the page's envelope, and the page fades
   up round it. Under it builder-b.js puts the card back open (_restoreOpenPose), so Back to Preview finds it.

   THE ADDRESS: what the builder knows when Next is pressed on Preview, never a placeholder. Top left the sender as the
   Send page's envelope shows it (ppbEnvFrom: Picturepops for To me, or the sender's lines once typed); the recipient
   only if a name or address has been typed already (ppbEnvTo, the non-placeholder lines). On a first pass through
   nobody has been typed yet, so the recipient area is left blank kraft and the Send page then asks for it.

   NEVER STUCK. Everything is fetched whole (blob) when Preview is reached, like the camera moves. Next waits at most
   900 ms for the close clip and the geometry; the envelope clip has the close's 3.4 s plus 1.5 s. A clip that is
   not in by then, will not decode, or will not play hands over to builder-b.js's CSS envelope from wherever the
   card is (open, or shut), so the customer always reaches Send. A frame callback that stops (a hidden tab) has a
   timer under it. Leaving Preview another way mid-move (Back, a step pill) ends it there and reopens the card.

   ?ppenvvid=0 turns this off in this browser (builder-b.js's CSS envelope, as on 17 Oct), ?ppenvvid=1 back.
   ?ppenvalpha=1 (this page load only) plays the Safari path, the side-by-side MP4 through WebGL, in any browser.
   ?ppenvfx=0 still turns the whole envelope off; reduced motion still skips it (both builder-b.js's gate).
   Loaded AFTER builder-b.js, which it wraps.
   ═══════════════════════════════════════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';
  const showStep = function (c) { return typeof c.ppbShowStep === 'function' ? c.ppbShowStep() : 'preview'; };

  /* ⛔ SAFARI STOPS CALLING requestVideoFrameCallback PARTWAY THROUGH A CLIP (18 Oct; found in the builder's opening
     with ?ppopendebug=1 on Daniel's Safari 26.6: the clip ran to its end while the card sat still, then snapped).
     afterFrame(v, cb) asks for the next video frame AND watches on rAF: when no video-frame callback has come for
     100ms while the clip plays, it calls cb with the clip's currentTime and stops asking that element for frame
     callbacks (v._ppRvfcDead), so the move goes on at the display's rate. cb(t, exact): exact is true only for a real
     video-frame callback (mediaTime). The same helper is in configurator.js, card-moves.js, builder-envelope.js and
     rd-card3d.js: change all four or none. */
  function afterFrame(v, cb) {
    var fired = false, t0 = performance.now();
    var rv = !!v.requestVideoFrameCallback && !v._ppRvfcDead;
    if (rv) v.requestVideoFrameCallback(function (now, meta) { if (fired) return; fired = true; cb(meta.mediaTime, true); });
    var poll = function () {
      if (fired) return;
      if (!rv || performance.now() - t0 > 100) {
        if (rv && !v.paused) v._ppRvfcDead = true;
        fired = true; cb(v.currentTime, false); return;
      }
      requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  }
  window.PP = window.PP || {};
  const PP = window.PP;
  if (typeof PP.builderB !== 'function') return;

  const ON = (function () {
    try {
      const m = /[?&]ppenvvid=([01])(?:&|$)/.exec(location.search);
      if (m) { if (m[1] === '0') localStorage.setItem('pp_ppenvvid', '0'); else localStorage.removeItem('pp_ppenvvid'); }
      return localStorage.getItem('pp_ppenvvid') !== '0';
    } catch (e) { return true; }
  })();
  const U = window.PP_ENVELOPE;
  if (!ON || !U) return;

  const FPS = 30;
  const CLOSE_N = 101, ENV_N = 122;
  const PAD = 0.98;            // the envelope's framing: the clip's square, nearly edge to edge of the stage
  const CLOSE_WAIT = 900;      // ms Next waits for the close clip and the geometry before taking the CSS envelope
  const ENV_WAIT = 1500;       // ms past the close the envelope clip may still take
  const HOLD_END = 200;        // ms on the landed envelope before it moves to the Send page's place
  const GLIDE = 720;           // ms for the landed envelope to become the Send page's (builder-b.css .ppb-envland)
  // The envelope's paper in the clip's last frame, as fractions of the 2000 square (envelope.webm frame 121, measured
  // 18 Oct: 1722 x 1305 at 139, 350); envelope-face.webp is this crop.
  const FACE = { x: 139 / 2000, y: 350 / 2000, w: 1722 / 2000, h: 1305 / 2000 };

  const wait = function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
  const twoFrames = function () { return new Promise(function (r) { requestAnimationFrame(function () { requestAnimationFrame(r); }); }); };
  /* Where the Send page's envelope will be, in the stage's coordinates: the page laid out for a moment, unseen (the CSS
     envelope's way, builder-b.js), while nothing moves. */
  const sendEnvRect = function (page, stage) {
    if (!page || !stage) return null;
    const keep = page.getAttribute('style') || '';
    page.style.setProperty('display', 'block', 'important'); page.style.visibility = 'hidden';
    const e = page.querySelector('.ppb-env');
    const r = e && e.getBoundingClientRect(), sr = stage.getBoundingClientRect();
    page.setAttribute('style', keep);
    return (r && r.width) ? { left: r.left - sr.left, top: r.top - sr.top, width: r.width, height: r.height } : null;
  };
  // A still copy of the page's envelope: Alpine's bindings off, the lines as typed, no placeholders (the clip has none).
  const copyOf = function (env) {
    const f = env.cloneNode(true);
    [f].concat([].slice.call(f.querySelectorAll('*'))).forEach(function (el) {
      [].slice.call(el.attributes).forEach(function (at) { if (/^(x-|:|@)/.test(at.name)) el.removeAttribute(at.name); });
    });
    f.querySelectorAll('template, .is-ph').forEach(function (el) { el.remove(); });
    f.removeAttribute('style');
    f.style.width = '100%'; f.style.height = '100%'; f.style.margin = '0';
    // The paper as an <img>, decoded before the copy is shown: a CSS background paints a frame or two late, plain kraft.
    const im = document.createElement('img');
    im.className = 'ppb-envland__face'; im.alt = ''; im.src = faceUrl();
    f.insertBefore(im, f.firstChild);
    return f;
  };
  /* The Send page's envelope picture, by the URL builder-b.css asks for (its own folder, no ?v): the page is hidden
     until Send, so the browser would not fetch it before the copy of it is laid over the clip. */
  let FACE_IMG = null;
  const faceUrl = function () {
    const l = document.querySelector('link[href*="builder-b.css"]');
    return l ? l.href.replace(/builder-b\.css[^]*$/, 'envelope-face.webp') : null;
  };
  const faceReady = function () {
    if (!FACE_IMG) {
      const u = faceUrl();
      if (!u) return Promise.resolve();
      const im = new Image();
      im.src = u;
      FACE_IMG = (im.decode ? im.decode() : new Promise(function (r, j) { im.onload = r; im.onerror = j; })).then(function () { return true; }, function () { return false; });
    }
    return FACE_IMG;
  };
  const ease = function (u) { return u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2; };
  const lerpCam = function (a, b, u) {
    return { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u, scale: a.scale * Math.pow(b.scale / a.scale, u) };
  };

  /* WebM with alpha where the engine draws it: Chrome, Edge, Firefox (and Chromium on Android). Never on iOS (every
     browser there is WebKit) and never in Safari, which ignores VP9's alpha. */
  const WEBM = (function () {
    const ua = navigator.userAgent || '';
    // ?ppenvalpha=1: the Safari path (side-by-side MP4 through WebGL) in any browser, to check it from Chrome.
    if (/[?&]ppenvalpha=1(?:&|$)/.test(location.search)) return false;
    if (/iPhone|iPad|iPod/.test(ua) || !/Chrome\/|Firefox\//.test(ua)) return false;
    try { return document.createElement('video').canPlayType('video/webm; codecs="vp9"') !== ''; } catch (e) { return false; }
  })();

  // Fetched once, played from memory (card-moves.js does the same): a clip that streams stalls on its first frames.
  const JSONS = {}, BLOBS = {};
  const loadJSON = function (url) {
    if (!JSONS[url]) {
      JSONS[url] = fetch(url).then(function (r) { if (!r.ok) throw new Error('env_geo_' + r.status); return r.json(); });
      JSONS[url].catch(function () { delete JSONS[url]; });
    }
    return JSONS[url];
  };
  const loadBlob = function (url) {
    if (!BLOBS[url]) {
      BLOBS[url] = fetch(url).then(function (r) { if (!r.ok) throw new Error('env_clip_' + r.status); return r.blob(); })
        .then(function (b) { return URL.createObjectURL(b); });
      BLOBS[url].catch(function () { delete BLOBS[url]; });
    }
    return BLOBS[url];
  };
  // Resolves to the value, or null once `ms` have passed (the fetch carries on, for the next time).
  const within = function (p, ms) {
    return Promise.race([p.then(function (v) { return v; }, function () { return null; }), wait(ms).then(function () { return null; })]);
  };

  // A video that will decode: resolves once it has a frame, rejects on an error or after `ms`.
  const ready = function (vid, ms) {
    return new Promise(function (res, rej) {
      if (vid.readyState >= 2) { res(); return; }
      const done = function (ok) {
        clearTimeout(t);
        vid.removeEventListener('loadeddata', onOk);
        vid.removeEventListener('error', onErr);
        if (ok) res(); else rej(new Error('env_decode'));
      };
      const onOk = function () { done(true); }, onErr = function () { done(false); };
      const t = setTimeout(function () { done(vid.readyState >= 2); }, ms);
      vid.addEventListener('loadeddata', onOk);
      vid.addEventListener('error', onErr);
    });
  };

  /* Play `vid` from 0 and call frame(i) on every presented frame, i the clip frame by its own timestamp. rVFC for the
     lockstep, and a timer under it, so a tab whose frame callbacks stop still reaches the end where the clip is.
     Resolves at the last frame; rejects if play() is refused. `alive()` false stops it (resolved with false). */
  const playLocked = function (vid, n, frame, alive) {
    return new Promise(function (res, rej) {
      let seq = 0, last = -1, over = false;
      const end = function (v) { if (over) return; over = true; seq++; res(v); };
      const step = function (now, meta) {
        if (over) return;
        seq++;
        if (!alive()) { try { vid.pause(); } catch (e) { /* gone */ } end(false); return; }
        const t = meta ? meta.mediaTime : vid.currentTime;
        const i = Math.min(n - 1, Math.max(0, Math.round(t * FPS)));
        if (i !== last) { last = i; frame(i); }
        if (i >= n - 1 || vid.ended || (vid.paused && !meta && vid.currentTime > 0)) { if (last !== n - 1) frame(n - 1); end(true); return; }
        schedule();
      };
      const schedule = function () {
        const s = ++seq;
        afterFrame(vid, function (t, exact) { if (s === seq) step(null, exact ? { mediaTime: t } : undefined); });
        setTimeout(function () { if (s === seq) step(); }, 250);   // a hidden tab: rAF stops too
      };
      try { vid.currentTime = 0; } catch (e) { /* at 0 already */ }
      frame(0); last = 0;
      const p = vid.play();
      schedule();
      if (p && p.catch) p.catch(function (e) { if (!over) { over = true; seq++; rej(e || new Error('env_play')); } });
    });
  };

  /* Side-by-side H.264 (colour premultiplied | alpha) through WebGL: Yonatan's drawEnv, preview/index.html. */
  const glFor = function (cv) {
    if (cv._ppbGl) return cv._ppbGl;
    const gl = cv.getContext('webgl', { premultipliedAlpha: true, alpha: true });
    if (!gl) return null;
    const sh = function (type, src) { const o = gl.createShader(type); gl.shaderSource(o, src); gl.compileShader(o); return o; };
    const pr = gl.createProgram();
    gl.attachShader(pr, sh(gl.VERTEX_SHADER, 'attribute vec2 p; varying vec2 v; void main(){ v = (p + 1.0) * 0.5; gl_Position = vec4(p, 0.0, 1.0); }'));
    gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, 'precision mediump float; uniform sampler2D t; varying vec2 v; void main(){ vec2 u = vec2(v.x * 0.5, 1.0 - v.y); vec3 c = texture2D(t, u).rgb; float a = texture2D(t, u + vec2(0.5, 0.0)).r; gl_FragColor = vec4(min(c, vec3(a)), a); }'));
    gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) return null;
    gl.useProgram(pr);
    const b = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(pr, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    [[gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE], [gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE], [gl.TEXTURE_MIN_FILTER, gl.LINEAR],
     [gl.TEXTURE_MAG_FILTER, gl.LINEAR]].forEach(function (kv) { gl.texParameteri(gl.TEXTURE_2D, kv[0], kv[1]); });
    cv._ppbGl = gl;
    return gl;
  };

  // The clips this device plays, decided once, on Preview, so Next finds the same files already in memory.
  let PICK = null;
  const pick = function (small) {
    if (!PICK) {
      PICK = {
        close: small ? U.closeSm : U.close,
        env: WEBM ? (small ? U.webmSm : U.webm) : (small ? U.alphaSm : U.alpha),
        webm: WEBM,
      };
    }
    return PICK;
  };

  const inner = PP.builderB;
  PP.builderB = function () {
    const c = inner.apply(this, arguments);
    const base = { ppbGo: c.ppbGo, into: c._ppbIntoEnvelope, init: c.init };
    if (typeof base.into !== 'function') return c;

    Object.assign(c, {
      // The envelope's framing: the clip's 2000 square (comp units, scaled like every plane) fitted to the stage.
      _ppbEnvCam() {
        const CS = PP.CardStage;
        if (!CS || !this.geom || !this.geom.comp) return null;
        const comp = this.geom.comp.width / (CS.LAYOUT_SCALE || 1);
        const cam = this._fitU({ cx: 0, cy: 0, w: comp, h: comp }, PAD);
        return (cam && cam.scale > 0) ? cam : null;
      },

      // Small clips when the screen draws the clip's square at 1200 device px or less (the opening's own rule).
      _ppbEnvSmall(cam) {
        const CS = PP.CardStage;
        const comp = this.geom && this.geom.comp ? this.geom.comp.width / ((CS && CS.LAYOUT_SCALE) || 1) : 0;
        const live = this._camLive() || cam;
        const s = Math.max(cam ? cam.scale : 0, live ? live.scale : 0);
        return comp * s * (window.devicePixelRatio || 1) <= 1200;
      },

      _ppbEnvPrefetch() {
        if (this._ppbEnvFetched) return;
        const cam = this._ppbEnvCam();
        if (!cam) return;
        this._ppbEnvFetched = true;
        const P = pick(this._ppbEnvSmall(cam));
        loadJSON(U.geo).catch(function () {});
        faceReady();
        loadBlob(P.close).catch(function () {}).then(function () { return loadBlob(P.env); }).catch(function () {});
        // The hand the addresses are written in, so the envelope never lands in a fallback face.
        try { if (document.fonts && document.fonts.load) document.fonts.load('500 20px Caveat').catch(function () {}); } catch (e) { /* drawn in cursive */ }
      },

      /* The step the envelope leaves from: Preview in the seven steps, Look in the five (builder-b.js SHOW_STEP, 19 Oct). */
      // Preview reached by any road (a step pill, the cart's Edit card, Next): the clips come in behind it.
      init() {
        const out = base.init ? base.init.apply(this, arguments) : undefined;
        const self = this;
        if (typeof this.$watch === 'function') {
          this.$watch(function () { return self.stage === 'builder' && self.ppbStep === showStep(self); }, function (on) {
            if (on) setTimeout(function () { try { self._ppbEnvPrefetch(); } catch (e) { /* the CSS envelope will do */ } }, 250);
          });
        }
        return out;
      },

      ppbGo(key) {
        if (key === showStep(this)) {
          const self = this;
          setTimeout(function () { try { self._ppbEnvPrefetch(); } catch (e) { /* the CSS envelope will do */ } }, 250);
        }
        return base.ppbGo.apply(this, arguments);
      },

      /* builder-b.js's ppbGo calls this once its gate has passed (Preview → Send, motion allowed, ?ppenvfx on). It
         resolves when the Send page is up (or Preview was left), and builder-b.js then reopens the card under it. */
      _ppbIntoEnvelope() {
        const self = this;
        return this._ppbEnvVideo().then(function (r) {
          if (r === 'fallback') return base.into.call(self);
          return r;
        });
      },

      _ppbEnvVideo() {
        const self = this, CS = PP.CardStage;
        const stage = document.querySelector('[data-pp-stage]'), zoom = document.querySelector('[data-pp-camera]');
        const alive = function () { return !run.cancel && self.stage === 'builder' && self.ppbStep === showStep(self); };
        const run = { cancel: false, els: [], vids: [] };
        const held = { drive: this._camDrive, release: this._camRelease };
        let holding = false;
        const hold = function () {
          if (holding) return;
          holding = true;
          self._camDrive = function () {};
          self._camRelease = function () { self.camNonce++; };
          self._ppbEnvUnhold = unhold;
        };
        const unhold = function () {
          if (!holding) return;
          holding = false;
          self._camDrive = held.drive;
          self._camRelease = held.release;
          if (self._ppbEnvUnhold === unhold) self._ppbEnvUnhold = null;
        };
        const drive = function (cam) { held.drive.call(self, cam); };
        const clear = function () {
          run.vids.forEach(function (v) { try { v.pause(); v.removeAttribute('src'); v.load(); } catch (e) { /* gone */ } v.remove(); });
          run.els.forEach(function (e) { e.remove(); });
          run.vids = []; run.els = [];
          if (stage) stage.classList.remove('ppb-envvid', 'ppb-envvid--env');
        };
        // The card as the CSS envelope (or Preview) expects it: the card geometry, shut or open, every plane back.
        const putBack = function (closed) {
          self.closing = false;
          self.cardClosed = !!closed;
          self.cardFrame = closed ? 100 : 0;
          try { self.applyFrame(self.cardFrame); } catch (e) { /* the next frame will */ }
        };
        const fallback = function (closed) {
          clear();
          putBack(closed);
          unhold();
          return 'fallback';
        };
        // Left Preview mid-move: nothing of the envelope stays, and the card is reopened by builder-b.js after this.
        const left = function () {
          clear();
          putBack(self.cardClosed || self.cardFrame > 0);
          unhold();
          self._camRelease();
          return 'left';
        };

        return (async function () {
          if (!stage || !zoom || !CS || !self.geom) return 'fallback';
          // Preview's own fold, if it is running (a Close or Open pressed just before Next), finishes first.
          const t0 = Date.now();
          while (self._previewAnim && Date.now() - t0 < 3000) await wait(40);
          if (!alive()) return 'left';
          self.previewZoom = false;
          if (self._pzRaf) { cancelAnimationFrame(self._pzRaf); self._pzRaf = 0; }
          if (self._previewStop) self._previewStop();

          const envCam = self._ppbEnvCam();
          if (!envCam) return 'fallback';
          const P = pick(self._ppbEnvSmall(envCam));
          const shut = !!self.cardClosed;
          // The close clip and the geometry, or the CSS envelope at once (nothing has moved yet).
          const got = await Promise.all([within(loadJSON(U.geo), CLOSE_WAIT), shut ? Promise.resolve('-') : within(loadBlob(P.close), CLOSE_WAIT)]);
          if (!alive()) return 'left';
          const eg = got[0], closeSrc = got[1];
          if (!eg || !closeSrc) return 'fallback';
          const envBlob = loadBlob(P.env);

          const W = stage.clientWidth, H = stage.clientHeight;
          const comp = self.geom.comp.width / (CS.LAYOUT_SCALE || 1);
          const dpr = window.devicePixelRatio || 1;
          const mkVid = function () {
            const v = document.createElement('video');
            v.className = 'pp-shadesrc';
            v.setAttribute('aria-hidden', 'true');
            v.muted = true; v.playsInline = true; v.preload = 'auto';
            v.setAttribute('muted', ''); v.setAttribute('playsinline', '');
            stage.appendChild(v);
            run.vids.push(v);
            return v;
          };
          const mkCanvas = function (cls) {
            const cv = document.createElement('canvas');
            cv.className = cls;
            cv.setAttribute('aria-hidden', 'true');
            stage.appendChild(cv);
            run.els.push(cv);
            return cv;
          };
          const place = function (el, cam) {
            const size = comp * cam.scale;
            el.style.width = size.toFixed(1) + 'px';
            el.style.height = size.toFixed(1) + 'px';
            el.style.left = (W / 2 + cam.x - size / 2).toFixed(1) + 'px';
            el.style.top = (H / 2 + cam.y - size / 2).toFixed(1) + 'px';
          };
          const startCam = self._camLive() || self.cameraTarget();
          hold();
          drive(startCam);
          stage.classList.add('ppb-envvid');

          // The envelope clip is loaded while the card closes, so the switch never waits on a decoder.
          const ev = mkVid();
          const envReady = envBlob.then(function (src) { ev.src = src; ev.load(); return ready(ev, ENV_WAIT + 4000); });
          envReady.catch(function () {});

          /* ── 1. THE CLOSE ──────────────────────────────────────────────────────────────────────────────────── */
          if (!shut) {
            const cv = mkVid();
            cv.src = closeSrc; cv.load();
            try { await ready(cv, 700); } catch (e) { return alive() ? fallback(false) : left(); }
            if (!alive()) return left();
            const shade = mkCanvas('pp-shadevid ppb-envshade');
            const px = Math.min(2048, Math.round(comp * Math.max(startCam.scale, envCam.scale) * dpr));
            shade.width = px; shade.height = px;
            const sx = shade.getContext('2d', { alpha: true });
            if (sx) { sx.imageSmoothingEnabled = true; sx.imageSmoothingQuality = 'high'; }
            self.closing = true;
            self.cardClosed = false;
            let at = 0;
            const frame = function (i) {
              at = i;
              self.cardFrame = i;
              self.applyFrame(i, null, true);
              const cam = lerpCam(startCam, envCam, ease(i / (CLOSE_N - 1)));
              drive(cam);
              place(shade, cam);
              if (sx && cv.videoWidth) sx.drawImage(cv, 0, 0, shade.width, shade.height);
            };
            let ok;
            try { ok = await playLocked(cv, CLOSE_N, frame, alive); } catch (e) { ok = null; }
            if (ok === false) return left();
            if (ok === null) {
              // play() refused: the card is shut where it is, and the CSS envelope takes it from there.
              if (at > 0) { self.cardFrame = 100; try { self.applyFrame(100, null, true); } catch (e) { /* below */ } }
              return fallback(at > 0);
            }
            self.closing = false;
            self.cardClosed = true;
            self.cardFrame = 100;
          } else {
            // Shut already (Preview's Close): the camera glides to the envelope's framing, the card on its frame 100.
            self.cardFrame = 100;
            try { self.applyFrame(100, null, true); } catch (e) { /* drawn below */ }
            const g0 = performance.now(), MS = 450;
            await new Promise(function (r) {
              const tick = function (now) {
                const u = Math.min(1, (now - g0) / MS);
                drive(lerpCam(startCam, envCam, ease(u)));
                if (u < 1 && alive()) requestAnimationFrame(tick); else r();
              };
              requestAnimationFrame(tick);
              setTimeout(r, MS + 400);
            });
            drive(envCam);
          }
          if (!alive()) return left();

          /* ── 2. THE ENVELOPE ───────────────────────────────────────────────────────────────────────────────── */
          try { await Promise.race([envReady, wait(ENV_WAIT).then(function () { throw new Error('env_late'); })]); }
          catch (e) { return alive() ? fallback(true) : left(); }
          if (!alive()) return left();

          const ecv = mkCanvas('ppb-envcv' + (shut ? ' is-fading' : ''));
          const epx = Math.min(2048, Math.round(comp * envCam.scale * dpr));
          ecv.width = epx; ecv.height = epx;
          place(ecv, envCam);
          let draw;
          if (P.webm) {
            const ex = ecv.getContext('2d', { alpha: true });
            if (!ex) return fallback(true);
            ex.imageSmoothingEnabled = true; ex.imageSmoothingQuality = 'high';
            draw = function () { ex.clearRect(0, 0, epx, epx); ex.drawImage(ev, 0, 0, epx, epx); };
          } else {
            const gl = glFor(ecv);
            if (!gl) return fallback(true);
            draw = function () {
              gl.viewport(0, 0, epx, epx);
              try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, ev); } catch (e) { return; }
              gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
            };
          }

          // The Address plane, on top of the video: the same camera as .pp-zoom, its own plane posed per frame.
          const layer = document.createElement('div');
          layer.className = 'ppb-envaddr';
          layer.setAttribute('aria-hidden', 'true');
          layer.style.transform = 'translate3d(' + envCam.x.toFixed(2) + 'px,' + envCam.y.toFixed(2) + 'px,0) scale(' + envCam.scale.toFixed(4) + ')';
          const plate = document.createElement('div');
          plate.className = 'ppb-envaddr__plane';
          plate.style.setProperty('--mm', (30 / (CS.LAYOUT_SCALE || 1)) + 'px');
          const lines = function (fn) {
            try { return (self[fn]() || []).filter(function (l) { return l && !l.ph && String(l.t || '').trim(); }).map(function (l) { return String(l.t); }); }
            catch (e) { return []; }
          };
          const block = function (cls, ls) {
            const d = document.createElement('div');
            d.className = 'ppb-envaddr__' + cls;
            ls.forEach(function (t) { const s = document.createElement('span'); s.textContent = t; d.appendChild(s); });
            return d;
          };
          const from = lines('ppbEnvFrom'), to = lines('ppbEnvTo');
          if (from.length) plate.appendChild(block('from', from));
          if (to.length) plate.appendChild(block('to', to));
          layer.appendChild(plate);
          stage.appendChild(layer);
          run.els.push(layer);

          // Only the cover travels; the rest of the card stays shut where it was, so it goes for the clip.
          const others = [].slice.call(stage.querySelectorAll('[data-pp-plane], [data-pp-plane-back]')).filter(function (el) {
            return el.getAttribute('data-pp-plane') !== 'CoverFront';
          });
          const hideOthers = function () { others.forEach(function (el) { el.style.display = 'none'; }); };
          const efr = function (i) {
            const t = CS.apply(stage, eg, i);
            hideOthers();
            const a = t.planes.Address;
            if (a) {
              plate.style.width = a.w + 'px';
              plate.style.height = a.h + 'px';
              plate.style.marginLeft = (-a.w / 2) + 'px';
              plate.style.marginTop = (-a.h / 2) + 'px';
              plate.style.transform = a.matrix;
              plate.style.display = a.facing ? '' : 'none';
            }
            draw();
          };
          stage.classList.add('ppb-envvid--env');
          // The close's shading leaves in the same frame the envelope's first frame (the same picture) arrives.
          let ok;
          try {
            ok = await playLocked(ev, ENV_N, function (i) {
              efr(i);
              if (i === 0) run.els.forEach(function (e) { if (e.classList && e.classList.contains('ppb-envshade')) e.style.display = 'none'; });
            }, alive);
          } catch (e) { ok = null; }
          if (ok === false) return left();
          if (ok === null) return fallback(true);

          /* ── 3. ONE ENVELOPE (Daniel, 18 Oct: "the envelope at the end [should] turn into the envelope at checkout -
             the same one as the render ... in one fluid motion"). The Send page's envelope IS the render's last frame
             (envelope-face.webp) with the addresses where the clip's Address plane writes them, so a copy of it laid
             exactly over the landed paper is the same picture. The copy takes over, glides and scales into the page's
             envelope, and only then the page fades up round it. ────────────────────────────────────────────────── */
          await wait(HOLD_END);
          if (!alive()) return left();
          const page = document.querySelector('.ppb-sendpage');
          const tr = sendEnvRect(page, stage);
          const real = page && page.querySelector('.ppb-env');
          let land = null;
          // The picture the copy is drawn with, decoded: a copy without it would be plain kraft for a moment.
          const faceOk = await within(faceReady(), 1200);
          if (!alive()) return left();
          if (tr && real && faceOk) {
            const size = comp * envCam.scale;
            const x0 = W / 2 + envCam.x - size / 2, y0 = H / 2 + envCam.y - size / 2;
            const lx = x0 + size * FACE.x, ly = y0 + size * FACE.y, lw = size * FACE.w;
            land = document.createElement('div');
            land.className = 'ppb-envland';
            land.setAttribute('aria-hidden', 'true');
            land.style.cssText = 'left:' + tr.left.toFixed(1) + 'px;top:' + tr.top.toFixed(1) + 'px;width:' + tr.width.toFixed(1) + 'px;height:' + tr.height.toFixed(1) + 'px;' +
              'transform:translate(' + (lx - tr.left).toFixed(2) + 'px,' + (ly - tr.top).toFixed(2) + 'px) scale(' + (lw / tr.width).toFixed(5) + ');';
            const cp = copyOf(real);
            land.appendChild(cp);
            stage.appendChild(land);
            run.els.push(land);
            const im = cp.querySelector('.ppb-envland__face');
            try { if (im && im.decode) await im.decode(); } catch (e) { /* drawn when it can be */ }
            await twoFrames();
            if (!alive()) return left();
            // The copy is on top, the same picture: the clip's canvas and the Address plane go under it.
            ecv.style.display = 'none';
            layer.style.display = 'none';
            land.classList.add('is-going');
            land.style.transform = 'none';
            await wait(GLIDE + 20);
            if (!alive()) return left();
          }
          if (page) page.classList.add('is-arriving');
          self.ppbGo('send');
          await twoFrames();
          if (page) page.classList.remove('is-arriving');
          await wait(420);
          clear();
          // Under the page: the card's own planes back (shut), the camera handed back by builder-b.js's unhold.
          putBack(true);
          return 'sent';
        })().catch(function (e) {
          // Anything unforeseen: no envelope left behind, and the CSS envelope from where the card is.
          if (!alive()) return left();
          return fallback(self.cardClosed || self.cardFrame > 50);
        });
      },
    });
    return c;
  };
})();
